import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Paper,
  Divider,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { exposureService } from '../services/exposureService';
import { authService } from '../services/authService';
import { dataService } from '../services/dataService';
import ExposureCard from '../components/ExposureCard';
import { resolveActivePlatform } from '../utils/resolveActivePlatform';
import { tokens } from '../theme/tokens';

const TEXT_PRIMARY = tokens.text.primary;
const TEXT_MUTED = tokens.text.muted;
const PAGE_BG = tokens.bg.app;
const FILTER_WELL_TINT = tokens.filterWell;
const RISK_HIGH_BG = tokens.risk.highBg;
const RISK_MED_BG = tokens.risk.mediumBg;
const RISK_LOW_BG = tokens.risk.lowBg;

const paperCardSx = {
  borderRadius: `${tokens.radius.card}px`,
  border: `1px solid ${tokens.border.strong}`,
  boxShadow: '0 10px 28px rgba(2, 6, 23, 0.42), 0 1px 3px rgba(2, 6, 23, 0.24)',
  backgroundColor: tokens.bg.card,
};

const btnPrimarySx = {
  minHeight: 44,
  px: 2.5,
  textTransform: 'none',
  fontWeight: 600,
  bgcolor: tokens.accent.brandBlue,
  color: '#fff',
  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.32)',
  transition: 'background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
  '&:hover': {
    bgcolor: '#1D4ED8',
    boxShadow: '0 6px 20px rgba(59, 130, 246, 0.42)',
    transform: 'translateY(-1px)',
  },
};

const btnSecondarySx = {
  minHeight: 44,
  px: 2.25,
  textTransform: 'none',
  fontWeight: 600,
  color: TEXT_PRIMARY,
  borderColor: tokens.border.strong,
  bgcolor: `${tokens.bg.card}f2`,
  transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    borderColor: tokens.border.strong,
    bgcolor: tokens.bg.cardElevated,
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
  },
};

const RISK_SORT_ORDER = { high: 0, medium: 1, low: 2 };

/**
 * Privacy exposures: filtered list with ExposureCard; Facebook ad insights below when applicable.
 */
const ExposuresPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [exposures, setExposures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterRisk, setFilterRisk] = useState('all');
  /** Facebook only: all | comment | post */
  const [filterSource, setFilterSource] = useState('all');
  /** Client-only: all | open | resolved */
  const [filterResolved, setFilterResolved] = useState('all');
  /** Client-only: date_desc | date_asc | risk_high */
  const [sortBy, setSortBy] = useState('date_desc');
  const [activePlatform, setActivePlatform] = useState('reddit');

  useEffect(() => {
    loadExposures();
  }, []);

  useEffect(() => {
    const r = searchParams.get('filterResolved');
    if (r === 'resolved' || r === 'open') {
      setFilterResolved(r);
    }
    const t = searchParams.get('type');
    if (t && ['email', 'phone', 'address_hint', 'location', 'address'].includes(t)) {
      setFilterType(t === 'address' ? 'address_hint' : t);
    }
  }, [searchParams]);

  const loadExposures = async (quiet = false) => {
    try {
      if (!quiet) {
        setLoading(true);
      }
      setError(null);

      const userData = await authService.getCurrentUser();
      if (!userData) {
        setError('Not authenticated. Please log in again.');
        if (!quiet) {
          setLoading(false);
        }
        navigate('/login');
        return;
      }

      const username =
        userData?.username ||
        userData?.reddit_username ||
        userData?.youtube_channel_title ||
        userData?.facebook_name;
      const platform = resolveActivePlatform(userData);
      const userId = userData?.id;
      const privacyParams = { platform, ...(userId ? { user_id: userId } : {}) };

      if (!username) {
        setError('Username not found. Please log in again.');
        if (!quiet) {
          setLoading(false);
        }
        navigate('/login');
        return;
      }

      setActivePlatform(platform);

      if (platform === 'facebook') {
        try {
          await dataService.reanalyzeFacebookFromStored(username);
        } catch (e) {
          console.warn('Facebook re-analyze from DB:', e?.response?.data || e?.message);
        }
      }

      const data = await exposureService.getExposures(username, privacyParams);
      setExposures(data || []);

      if (!quiet) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Load exposures error:', err);
      setError('Failed to load exposures');
      if (!quiet) {
        setLoading(false);
      }
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/login');
      }
    }
  };

  const refreshExposuresWithSync = async () => {
    try {
      setRefreshing(true);
      setError(null);
      const userData = await authService.getCurrentUser();
      if (!userData) {
        navigate('/login');
        return;
      }
      const platform = resolveActivePlatform(userData);
      const displayUser =
        userData?.username ||
        userData?.reddit_username ||
        userData?.youtube_channel_title ||
        userData?.facebook_name;

      if (platform === 'facebook' && displayUser) {
        try {
          await dataService.reanalyzeFacebookFromStored(displayUser);
        } catch (err) {
          console.error('Facebook sync on refresh failed:', err);
          setError(err.response?.data?.message || err.message || 'Could not refresh Facebook exposures from saved data.');
        }
      } else if (userData?.reddit_username) {
        try {
          await dataService.scanAndAnalyze(userData.reddit_username, {
            platform: 'reddit',
            limit_posts: 100,
            limit_comments: 100,
          });
        } catch (err) {
          console.error('Reddit sync on refresh failed:', err);
          setError(err.response?.data?.message || err.message || 'Could not sync latest Reddit activity.');
        }
      }
      await loadExposures(true);
    } finally {
      setRefreshing(false);
    }
  };

  const displayExposures = useMemo(() => {
    const filtered = exposures.filter((exposure) => {
      if (filterType !== 'all' && exposure.exposure_type !== filterType) {
        return false;
      }
      if (filterRisk !== 'all') {
        const exposureRisk = (exposure.risk_level || '').toLowerCase();
        if (exposureRisk !== filterRisk.toLowerCase()) {
          return false;
        }
      }
      if (activePlatform === 'facebook' && filterSource !== 'all') {
        if (filterSource === 'comment' && exposure.source !== 'comment') return false;
        if (filterSource === 'post' && exposure.source !== 'post') return false;
      }
      if (filterResolved === 'open' && exposure.is_resolved) return false;
      if (filterResolved === 'resolved' && !exposure.is_resolved) return false;
      return true;
    });

    const sorted = [...filtered];
    if (sortBy === 'date_desc') {
      sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    } else if (sortBy === 'date_asc') {
      sorted.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    } else if (sortBy === 'risk_high') {
      sorted.sort((a, b) => {
        const ra = (a.risk_level || '').toLowerCase();
        const rb = (b.risk_level || '').toLowerCase();
        return (RISK_SORT_ORDER[ra] ?? 99) - (RISK_SORT_ORDER[rb] ?? 99);
      });
    }
    return sorted;
  }, [
    exposures,
    filterType,
    filterRisk,
    filterSource,
    activePlatform,
    filterResolved,
    sortBy,
  ]);

  const uniqueTypes = [...new Set(exposures.map((e) => e.exposure_type))];
  const commentCount = exposures.filter((e) => e.source === 'comment').length;
  const postCount = exposures.filter((e) => e.source === 'post').length;

  const filterToolbarSx = {
    mt: 2,
    pt: 2.5,
    px: { xs: 2, sm: 2.5 },
    pb: 2.5,
    borderRadius: '12px',
    bgcolor: FILTER_WELL_TINT,
    border: '1px solid rgba(148, 163, 184, 0.3)',
    '& .MuiOutlinedInput-root': {
      borderRadius: `${tokens.radius.control}px`,
      bgcolor: tokens.bg.cardElevated,
      transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    },
    '& .MuiOutlinedInput-root:hover': {
      boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
    },
    '& .MuiOutlinedInput-root.Mui-focused': {
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.15)',
    },
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: PAGE_BG,
        }}
      >
        <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
          <Paper
            elevation={0}
            sx={{
              ...paperCardSx,
              py: 8,
              px: 3,
            }}
          >
            <CircularProgress size={56} thickness={4} sx={{ color: 'secondary.main' }} />
            <Typography variant="h6" sx={{ mt: 3, color: TEXT_MUTED, fontWeight: 600 }}>
              Loading exposures...
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: PAGE_BG,
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 }, pb: 6 }}>
        <Paper
          elevation={0}
          sx={{
            ...paperCardSx,
            p: { xs: 2, sm: 3 },
            mb: 3.5,
            background: tokens.hero.gradient,
            border: tokens.hero.border,
            boxShadow: tokens.hero.shadow,
            backdropFilter: 'blur(12px)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                  color: TEXT_PRIMARY,
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                }}
              >
                Privacy Exposures
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75, color: tokens.text.secondary, fontWeight: 500, maxWidth: 520 }}>
                Review findings from your last scan or export. Use filters to narrow the list.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                disabled={refreshing || loading}
                onClick={refreshExposuresWithSync}
                sx={btnSecondarySx}
              >
                {refreshing ? 'Syncing…' : 'Refresh'}
              </Button>
              <Button variant="contained" onClick={() => navigate('/dashboard')} sx={btnPrimarySx}>
                Back to Dashboard
              </Button>
            </Stack>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3.5, borderRadius: '12px' }}>
            {error}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            ...paperCardSx,
            p: { xs: 2, sm: 2.75 },
            mb: 3.5,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 1 }}
          >
            <Typography variant="body2" sx={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
              Total: <Box component="span" sx={{ fontWeight: 700 }}>{exposures.length}</Box>
            </Typography>
            <Typography variant="body2" sx={{ color: TEXT_MUTED, fontWeight: 500 }}>
              Showing:{' '}
              <Box component="span" sx={{ color: TEXT_PRIMARY, fontWeight: 700 }}>
                {displayExposures.length}
              </Box>
            </Typography>
            {activePlatform === 'facebook' && (
              <>
                <Chip
                  size="small"
                  label={`${commentCount} in comments`}
                  sx={{ bgcolor: 'rgba(148, 163, 184, 0.18)', color: TEXT_PRIMARY, fontWeight: 600, border: 'none' }}
                />
                <Chip
                  size="small"
                  label={`${postCount} in your posts`}
                  sx={{ bgcolor: 'rgba(148, 163, 184, 0.18)', color: TEXT_PRIMARY, fontWeight: 600, border: 'none' }}
                />
              </>
            )}
            <Chip
              label={`${exposures.filter((e) => (e.risk_level || '').toLowerCase() === 'high').length} High`}
              size="small"
              sx={{
                fontWeight: 700,
                bgcolor: RISK_HIGH_BG,
                color: '#B91C1C',
                border: '1px solid rgba(239, 68, 68, 0.35)',
                boxShadow: '0 0 0 1px rgba(239, 68, 68, 0.08)',
              }}
            />
            <Chip
              label={`${exposures.filter((e) => (e.risk_level || '').toLowerCase() === 'medium').length} Medium`}
              size="small"
              sx={{
                fontWeight: 700,
                bgcolor: RISK_MED_BG,
                color: '#B45309',
                border: '1px solid rgba(245, 158, 11, 0.4)',
                boxShadow: '0 0 0 1px rgba(245, 158, 11, 0.1)',
              }}
            />
            <Chip
              label={`${exposures.filter((e) => (e.risk_level || '').toLowerCase() === 'low').length} Low`}
              size="small"
              sx={{
                fontWeight: 700,
                bgcolor: RISK_LOW_BG,
                color: '#15803D',
                border: '1px solid rgba(34, 197, 94, 0.35)',
                boxShadow: '0 0 0 1px rgba(34, 197, 94, 0.08)',
              }}
            />
          </Stack>

          <Divider sx={{ borderColor: 'rgba(147, 180, 210, 0.45)', my: 2 }} />

          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: TEXT_PRIMARY, mb: 1.5, letterSpacing: '-0.01em' }}
          >
            Filters
          </Typography>

          <Box sx={filterToolbarSx}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              flexWrap="wrap"
              useFlexGap
              alignItems={{ xs: 'stretch', md: 'flex-start' }}
            >
              <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 160 }, flex: { md: '1 1 160px' } }}>
                <InputLabel id="exp-filter-type">Exposure type</InputLabel>
                <Select
                  labelId="exp-filter-type"
                  value={filterType}
                  label="Exposure type"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="all">All types</MenuItem>
                  {uniqueTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 160 }, flex: { md: '1 1 160px' } }}>
                <InputLabel id="exp-filter-risk">Risk level</InputLabel>
                <Select
                  labelId="exp-filter-risk"
                  value={filterRisk}
                  label="Risk level"
                  onChange={(e) => setFilterRisk(e.target.value)}
                >
                  <MenuItem value="all">All risk levels</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
              {activePlatform === 'facebook' && (
                <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 }, flex: { md: '1 1 180px' } }}>
                  <InputLabel id="exp-filter-source">Source</InputLabel>
                  <Select
                    labelId="exp-filter-source"
                    value={filterSource}
                    label="Source"
                    onChange={(e) => setFilterSource(e.target.value)}
                  >
                    <MenuItem value="all">All (comments &amp; posts)</MenuItem>
                    <MenuItem value="comment">Comments on others&apos; posts</MenuItem>
                    <MenuItem value="post">Your posts</MenuItem>
                  </Select>
                </FormControl>
              )}
              <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 160 }, flex: { md: '1 1 160px' } }}>
                <InputLabel id="exp-filter-status">Status</InputLabel>
                <Select
                  labelId="exp-filter-status"
                  value={filterResolved}
                  label="Status"
                  onChange={(e) => setFilterResolved(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="open">Open (needs attention)</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 }, flex: { md: '1 1 180px' } }}>
                <InputLabel id="exp-sort">Sort by</InputLabel>
                <Select labelId="exp-sort" value={sortBy} label="Sort by" onChange={(e) => setSortBy(e.target.value)}>
                  <MenuItem value="date_desc">Newest first</MenuItem>
                  <MenuItem value="date_asc">Oldest first</MenuItem>
                  <MenuItem value="risk_high">Risk (high to low)</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>
        </Paper>

        {displayExposures.length === 0 ? (
          <Alert
            severity="info"
            sx={{
              borderRadius: '16px',
              bgcolor: tokens.bg.card,
              color: TEXT_PRIMARY,
              border: '1px solid rgba(147, 180, 210, 0.5)',
              boxShadow: paperCardSx.boxShadow,
            }}
          >
            {exposures.length === 0
              ? 'No privacy exposures detected. Great job keeping your data private!'
              : 'No exposures match the selected filters.'}
          </Alert>
        ) : (
          <Stack spacing={3.5}>
            {displayExposures.map((exposure, index) => (
              <Box key={exposure._id || index}>
                <ExposureCard exposure={exposure} onUpdate={loadExposures} />
              </Box>
            ))}
          </Stack>
        )}

      </Container>
    </Box>
  );
};

export default ExposuresPage;
